// frontend/src/pages/portal/modifiers/ModifierGroupsTab.js
//
// Manager Portal — Option Groups tab (third tab on PortalProductsPage,
// alongside Products and Add-ons). Lists every modifier group the tenant has
// built as a card: name, selection-rule pill, its option pills (nested ones
// marked with a trailing ↳), and either how many products use the group
// directly or — for a group that only exists nested inside another add-on —
// where it's nested. Showing "nested in Corn" instead of "used by 0
// products" stops a tenant deleting a nested group thinking it's orphaned.
//
// Visual contract: docs/mockups/nested-addons/portal-group-builder.html
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { C, FONT } from '../../../styles/theme';
import useIsMobile from '../../../hooks/useIsMobile';
import { modifierService } from '../../../services/api';
import ModifierGroupEditor from './ModifierGroupEditor';
import { ruleLabel, isRequired } from './modifierRules';

const cardStyle = {
    background: C.white, border: `1px solid ${C.border}`, borderRadius: 13,
    padding: '15px 16px', textAlign: 'left', cursor: 'pointer', fontFamily: FONT,
    width: '100%', display: 'block',
};

function GroupCard({ group, addonMap, nestedAddonCodes, currency, t, onClick }) {
    const required = isRequired(group);
    const options = group.options || [];
    return (
        <button onClick={onClick} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 9, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14.5, fontWeight: 800, color: C.text }}>{group.name}</span>
                <span style={{
                    fontSize: 9.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase',
                    borderRadius: 6, padding: '3px 8px',
                    color: required ? C.red : C.muted,
                    background: required ? C.redLight : C.bg,
                    border: `1px solid ${required ? C.redBorder : C.border}`,
                }}>{ruleLabel(group, t)}</span>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 11 }}>
                {options.length === 0 ? (
                    <span style={{ fontSize: 11.5, color: C.mutedLight }}>
                        {t('no_options_yet', {}, 'No options yet')}
                    </span>
                ) : options.map(o => {
                    const addon = addonMap[o.addon_product_code];
                    const nested = nestedAddonCodes.has(o.addon_product_code);
                    return (
                        <span key={o.addon_product_code} style={{
                            fontSize: 11.5, fontWeight: nested ? 700 : 600, borderRadius: 999, padding: '3px 10px',
                            color: nested ? C.blue : C.muted, background: nested ? C.blueLight : C.bg,
                            border: `1px solid ${nested ? C.blueBorder : C.border}`,
                        }}>
                            {addon ? addon.name : o.addon_product_code}
                            {nested ? ' ↳' : ''}
                            {addon && addon.price ? ` ${currency}${Number(addon.price).toFixed(2)}` : ''}
                        </span>
                    );
                })}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11.5, fontWeight: 700, flexWrap: 'wrap' }}>
                {group.nested_in ? (
                    <span style={{
                        color: C.blue, background: C.blueLight, border: `1px solid ${C.blueBorder}`,
                        borderRadius: 999, padding: '2px 9px',
                    }}>
                        ↳ {t('nested_in', { name: group.nested_in }, `nested in ${group.nested_in}`)}
                    </span>
                ) : (
                    <span style={{
                        color: C.greenDark, background: C.greenLight, border: `1px solid ${C.greenBorder}`,
                        borderRadius: 999, padding: '2px 9px',
                    }}>
                        {t('used_by')} {group.used_by_products || 0}{' '}
                        {(group.used_by_products === 1) ? t('product') : t('products')}
                    </span>
                )}
            </div>
        </button>
    );
}

export default function ModifierGroupsTab({ addons = [], currency = '', t, toast }) {
    const isMobile = useIsMobile();
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editorState, setEditorState] = useState({ open: false, group: null });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await modifierService.getGroups();
            setGroups(data || []);
        } catch {
            toast && toast(t('failed_to_load_groups', {}, 'Failed to load option groups'), 'error');
        } finally {
            setLoading(false);
        }
    }, [t, toast]);

    useEffect(() => { load(); }, [load]);

    const addonMap = useMemo(() => {
        const m = {};
        (addons || []).forEach(a => { m[a.product_code] = a; });
        return m;
    }, [addons]);

    // Every add-on that already carries a nested group somewhere in the
    // tenant's catalog — used to mark option pills with ↳ on the card.
    const nestedAddonCodes = useMemo(() => {
        const codes = new Set();
        groups.forEach(g => {
            if (!g.nested_in) return;
            const match = (addons || []).find(a => a.name === g.nested_in);
            if (match) codes.add(match.product_code);
        });
        return codes;
    }, [groups, addons]);

    const openEditor = (group) => setEditorState({ open: true, group: group || null });
    const closeEditor = () => setEditorState({ open: false, group: null });
    const handleSaved = () => { closeEditor(); load(); };

    const newGroupBtn = (
        <button onClick={() => openEditor(null)} style={{
            padding: '9px 18px', borderRadius: 10, border: 'none', background: C.green,
            color: C.white, fontSize: 13, fontWeight: 700, cursor: 'pointer',
        }}>
            {t('new_group', {}, '＋ New group')}
        </button>
    );

    return (
        <div style={{ fontFamily: FONT }}>
            <div className="portal-wrap" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
                {newGroupBtn}
            </div>

            {loading ? (
                <div style={{
                    background: C.white, border: `1px solid ${C.border}`, borderRadius: 12,
                    padding: 48, textAlign: 'center', color: C.mutedLight, fontSize: 14,
                }}>
                    {t('loading')}
                </div>
            ) : groups.length === 0 ? (
                <div style={{
                    background: C.white, border: `1px solid ${C.border}`, borderRadius: 12,
                    padding: 56, textAlign: 'center',
                }}>
                    <div style={{
                        width: 52, height: 52, borderRadius: '50%', background: C.greenLight,
                        margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="1.5">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="12" y1="8" x2="12" y2="16" />
                            <line x1="8" y1="12" x2="16" y2="12" />
                        </svg>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.text, marginBottom: 6 }}>
                        {t('no_modifier_groups_yet', {}, 'No option groups yet')}
                    </div>
                    <div style={{ fontSize: 13, color: C.muted, marginBottom: 20 }}>
                        {t('modifier_groups_empty_desc', {}, 'Build a reusable choice — like a flavour or a spice level — once, then attach it wherever it applies.')}
                    </div>
                    <button onClick={() => openEditor(null)} style={{
                        padding: '9px 20px', borderRadius: 8, border: 'none', background: C.green,
                        color: C.white, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}>
                        {t('create_first_group', {}, 'Create your first group')}
                    </button>
                </div>
            ) : (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: 12,
                }}>
                    {groups.map(g => (
                        <GroupCard
                            key={g.group_id}
                            group={g}
                            addonMap={addonMap}
                            nestedAddonCodes={nestedAddonCodes}
                            currency={currency}
                            t={t}
                            onClick={() => openEditor(g)}
                        />
                    ))}
                </div>
            )}

            <ModifierGroupEditor
                open={editorState.open}
                group={editorState.group}
                allGroups={groups}
                addons={addons}
                currency={currency}
                onClose={closeEditor}
                onSaved={handleSaved}
                t={t}
                toast={toast}
            />
        </div>
    );
}
